import { isBrowserAnalyticsCaptureAllowed } from "./analyticsContext";
import type { CaptureResult } from "posthog-js";

import { withAnalyticsEnvironmentContext } from "./analyticsContext";
import { enforceEvidenceRankPostHogPrivacy } from "./evidenceRankAnalyticsPrivacy";
import {
  safeBrowserTelemetryDistinctId,
  isSafeBrowserTelemetryIdentity,
  sanitizeTelemetryValue,
} from "./telemetryPrivacy";
import { isPrivateTelemetrySession } from "./telemetrySessionPrivacy";

const SDK_IDENTITY_FIELDS = new Set([
  "distinctid", "anondistinctid", "anonymousid", "peopledistinctid", "userid", "deviceid", "alias",
]);

function stripUnsafeSdkIdentities(value: unknown, key = ""): unknown {
  if (SDK_IDENTITY_FIELDS.has(key.replace(/[^a-z0-9]/gi, "").toLowerCase())) {
    return isSafeBrowserTelemetryIdentity(value) ? value : undefined;
  }
  if (Array.isArray(value)) return value.map((item) => stripUnsafeSdkIdentities(item));
  // Rebuilding a Date yields `{}`, which PostHog rejects as an event timestamp.
  if (value instanceof Date) return value;
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).flatMap(([childKey, child]) => {
      const safe = stripUnsafeSdkIdentities(child, childKey);
      return safe === undefined ? [] : [[childKey, safe]];
    }));
  }
  return value;
}

/** Flags have their own request path; sanitize properties before SDK retention. */
export function sanitizePostHogFlagProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  return sanitizeTelemetryValue(stripUnsafeSdkIdentities(properties)) as Record<string, unknown>;
}

/** Clean legacy browser cache before the SDK can build flags requests from it. */
export function preparePostHogPersistence(apiKey: string): boolean {
  const key = `ph_${apiKey.replace(/\+/g, "PL").replace(/\//g, "SL").replace(/=/g, "EQ")}_posthog`;
  try {
    for (const storage of [window.localStorage, window.sessionStorage]) {
      const serialized = storage.getItem(key);
      if (!serialized) continue;
      const parsed: unknown = JSON.parse(serialized);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        storage.removeItem(key);
        continue;
      }
      const saved = parsed as Record<string, unknown>;
      const unsafeIdentity = saved.distinct_id !== undefined &&
        !isSafeBrowserTelemetryIdentity(saved.distinct_id);
      const safe = sanitizeTelemetryValue(stripUnsafeSdkIdentities({
        ...saved,
        ...(unsafeIdentity ? {
          distinct_id: safeBrowserTelemetryDistinctId(saved.distinct_id),
          $user_state: "anonymous",
        } : {}),
      }));
      storage.setItem(key, JSON.stringify(safe));
    }
    return true;
  } catch {
    // Memory persistence avoids reading a legacy cache we could not sanitize.
    return false;
  }
}

/**
 * Final browser boundary for PostHog events.
 *
 * PostHog invokes this after it has merged registered super-properties and
 * SDK-generated URL fields. Keep the sanitizer here so custom events,
 * autocapture, pageviews, and person-property events share one last redaction
 * pass immediately before transport.
 */
export function sanitizePostHogCapture(
  capture: CaptureResult | null,
): CaptureResult | null {
  if (!capture || !isBrowserAnalyticsCaptureAllowed()) return null;
  if (isPrivateTelemetrySession() &&
      ["$autocapture", "$snapshot"].includes(capture.event)) return null;

  // An unsafe alias is never replaced with a common constant or linked to a person.
  if (capture.event === "$create_alias" && (
    !isSafeBrowserTelemetryIdentity(capture.properties?.alias) ||
    !isSafeBrowserTelemetryIdentity(capture.properties?.distinct_id)
  )) return null;

  const rawDistinctId = capture.properties?.distinct_id;
  const safeDistinctId = typeof rawDistinctId === "string"
    ? safeBrowserTelemetryDistinctId(rawDistinctId)
    : rawDistinctId;
  const properties = safeDistinctId !== rawDistinctId
    ? {
        ...capture.properties,
        distinct_id: safeDistinctId,
      }
    : capture.properties;

  const sanitized = sanitizeTelemetryValue(stripUnsafeSdkIdentities({
    ...capture,
    properties: withAnalyticsEnvironmentContext(properties),
  })) as CaptureResult;

  return enforceEvidenceRankPostHogPrivacy(sanitized);
}
